"use client";
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Icon } from "@zapier/zinnia-icons";
import {
  Field,
  FormLabel,
  TextInput,
  VisuallyHidden,
} from "@zapier/design-system";
import { useRouterParams } from "../../../hooks";

import styles from "./SearchInput.module.css";

interface SearchInputProps {
  disabledText?: string;
  isDisabled?: boolean;
  onSearch: (search: string) => void;
  placeholder: string;
}

function SearchInput({
  disabledText,
  isDisabled,
  onSearch,
  placeholder,
}: SearchInputProps) {
  const { search: searchValue, setParams } = useRouterParams();
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    setSearch(searchValue || "");
  }, [searchValue]);

  const submitSearch = (event: KeyboardEvent<HTMLElement>) => {
    const value = (event as any).target.value;
    if (event.key === "Enter") {
      setSearch(value);
      onSearch(value);
    }
  };

  const clearSearch = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setSearch("");
      setParams({}, ["search", "page"]);
    },
    [setParams, setSearch],
  );

  const handleChangeSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const query = event.target.value;
      setSearch(query);
    },
    [setSearch],
  );

  return (
    <div className={styles.root}>
      <Field
        label={placeholder}
        inputId="search-input"
        renderLabel={(labelProps) => (
          <VisuallyHidden>
            <FormLabel {...labelProps}>{placeholder}</FormLabel>
          </VisuallyHidden>
        )}
        renderInput={(inputProps) => (
          <TextInput
            {...inputProps}
            id="search-input"
            aria-label={placeholder}
            isDisabled={isDisabled}
            disabledText={disabledText}
            onKeyDown={submitSearch}
            placeholder={placeholder}
            type="search"
            value={search}
            onChange={handleChangeSearch}
            renderIconBefore={({ iconSize }) => (
              <Icon name="navSearch" size={iconSize} />
            )}
            renderIconAfter={({ iconSize, isFocused, isHovered }) => {
              const iconColor =
                isFocused || isHovered ? undefined : "GrayWarm6";
              return search ? (
                <button
                  className={styles.clearButton}
                  onClick={clearSearch}
                  aria-label="Clear search"
                >
                  <Icon
                    canAcceptPointerEvents={false}
                    color={iconColor}
                    name="formX"
                    size={iconSize}
                  />
                </button>
              ) : null;
            }}
          />
        )}
      />
    </div>
  );
}

export default SearchInput;
